import asyncRetry from "async-retry";
import { getErrorMessage } from "replo-utils/lib/errors";
import { ReploError } from "schemas/reploError";

class FetchRetryStoppedError extends ReploError {}

const MAX_ATTEMPTS = 3;
const BASE_DELAY_MS = 50;

const RETRYABLE_NETWORK_PATTERNS = [
  "network connection lost",
  "timed out",
  "timeout",
  "fetch failed",
  "connection reset",
  "econnreset",
  "econnrefused",
  "enotfound",
  "socket hang up",
  // Browser fetch failures (Chrome, Safari, Firefox), for browser bundles.
  "failed to fetch",
  "load failed",
  "networkerror when attempting to fetch",
] as const;

export function isRetryableNetworkError(error: unknown): boolean {
  const message = getErrorMessage(error).toLowerCase();
  return RETRYABLE_NETWORK_PATTERNS.some((pattern) => {
    return message.includes(pattern);
  });
}

/**
 * Retries fetches that fail with transient Cloudflare/network errors
 * (e.g. "Network connection lost") before surfacing the failure. Only thrown
 * network errors are retried — HTTP error responses are returned as-is, so
 * there is no retry amplification against an origin that is up but failing.
 *
 * @see REPL-28916
 */
export async function fetchWithRetry(
  input: Request | URL | string,
  init: RequestInit | undefined,
  {
    label,
  }: {
    label: string;
  },
): Promise<Response> {
  let requestUrl: string;
  if (typeof input === "string") {
    requestUrl = input;
  } else if (input instanceof URL) {
    requestUrl = input.toString();
  } else {
    requestUrl = input.url;
  }

  const startedAt = Date.now();

  const response = await asyncRetry(
    async (bail, attempt) => {
      try {
        return await fetch(input, init);
      } catch (error) {
        const retryable = isRetryableNetworkError(error);
        // NOTE (Gabe, 2026-08-18): console.error, not replo-console-logger —
        // that logger pulls in node:async_hooks, and this module reaches browser
        // bundles through agent-http-client -> bedrock-sdk.
        console.error("Origin fetch failed", {
          label,
          requestUrl,
          attempt,
          maxAttempts: MAX_ATTEMPTS,
          retryable,
          willRetry: retryable && attempt < MAX_ATTEMPTS,
          elapsedMs: Date.now() - startedAt,
          errorName: error instanceof Error ? error.name : typeof error,
          errorMessage: getErrorMessage(error),
        });
        // On exhaustion, node-retry otherwise chooses the most frequent error.
        if (!retryable || attempt === MAX_ATTEMPTS) {
          bail(error);
          return;
        }
        throw error;
      }
    },
    {
      retries: MAX_ATTEMPTS - 1,
      minTimeout: BASE_DELAY_MS,
      randomize: false,
    },
  );
  if (response === undefined) {
    throw new FetchRetryStoppedError({
      message: "Fetch retry stopped without a response",
    });
  }
  return response;
}
